import { useState, useEffect, useCallback } from 'react';
import apiClient from '../../../api';

export const useSync = (user, onSyncSuccess) => {
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncReport, setSyncReport] = useState(null);
    const [syncDisabled, setSyncDisabled] = useState(false);
    const [timeLeft, setTimeLeft] = useState(null);

    const COOLDOWN_TIME = 60 * 60 * 1000; 

    const checkCooldown = useCallback(() => {
        if (!user?.cliente_id) return;

        const storageKey = `@escuta/last_sync_${user.cliente_id}`;
        const lastSync = localStorage.getItem(storageKey);

        if (lastSync) {
            const timePassed = Date.now() - parseInt(lastSync, 10);
            if (timePassed < COOLDOWN_TIME) {
                setSyncDisabled(true);
                setTimeLeft(COOLDOWN_TIME - timePassed);
            } else {
                setSyncDisabled(false);
                setTimeLeft(null);
            }
        }
    }, [user?.cliente_id]);

    useEffect(() => {
        checkCooldown();
        const interval = setInterval(checkCooldown, 1000);
        return () => clearInterval(interval);
    }, [checkCooldown]);

    /**
     * onFinishCallback -> executa SEMPRE (sucesso ou erro)
     */
    const executeSync = async (
        startDate = null,
        endDate = null,
        onFinishCallback
    ) => {
        if (isSyncing || syncDisabled) return;

        setIsSyncing(true);
        setSyncReport(null);

        try {
            const payload =
                startDate && endDate
                    ? { start_date: startDate, end_date: endDate }
                    : {};

            const response = await apiClient.post(
                `/api/social/sync/${user.cliente_id}`,
                payload
            );

            const { successes } = response.data.details;

            // ✅ sucesso total ou parcial
            if (successes && successes.length > 0) {
                localStorage.setItem(
                    `@escuta/last_sync_${user.cliente_id}`,
                    Date.now().toString()
                );

                checkCooldown();

                if (onSyncSuccess) onSyncSuccess();
            }

            setSyncReport(response.data.details);
        } catch (err) {
            const errorData = err.response?.data;
            const status = err.response?.status;

            if (status === 422 || errorData?.error === 'VOLUME_EXCEEDED') {
                setSyncReport({
                    successes: [],
                    failures: [
                        `⚠️ ${errorData.message || 'Limite de volume excedido.'}`,
                        errorData.details || 'Tente um período menor.'
                    ]
                });
            } else if (status === 429) {
                setSyncReport({
                    successes: [],
                    failures: [
                        errorData?.error || 'Aguarde para sincronizar novamente.'
                    ]
                });
                checkCooldown();
            } else {
                setSyncReport({
                    successes: [],
                    failures: [
                        errorData?.error || 'Erro crítico ao sincronizar.'
                    ]
                });
            }
        } finally {
            setIsSyncing(false);

            // ✅ FECHA O MODAL SEMPRE
            if (onFinishCallback) {
                onFinishCallback();
            }

            setTimeout(() => setSyncReport(null), 20000);
        }
    };

    return {
        isSyncing,
        syncReport,
        syncDisabled,
        timeLeft,
        executeSync
    };
};